import { IStorage, InjectStorage, STORAGE } from "@whatslly/commons/storage.constants";
import { RefreshUserResponseType } from "@whatslly/types/RefreshUserResponse";
import { AuthResponseType } from "@whatslly/types/AuthResponse";
import { QueryResponseType } from "@whatslly/types/QueryResponse";
import { getJSON } from "@whatslly/commons/apiUtils";
import { FetchMethod, container } from "../di";
import { FrontLogger } from "@whatslly/commons/frontLogger/frontLogger";
import { AppIds, LoginProviderType } from "@whatslly/types/LoginProviderType";
import { AppsNamesType } from "@whatslly/types/AppsNames";
import { updateDLPPoliciesService } from "@whatslly/commons/UpdateDLPPoliciesService";
import { updateKeywordPoliciesService } from "@whatslly/commons/UpdateKeywordPoliciesService";
import { updateUrlPoliciesService } from "@whatslly/commons/UpdateUrlPoliciesService";

const providers: Record<string, (typeof AppIds)[keyof typeof AppIds]> = {
  hubspot: AppIds.HUBSPOT,
  msdynamics: AppIds.MICROSOFT_DYNAMICS,
  salesforce: AppIds.SALESFORCE,
  google: AppIds.GOOGLE,
  saml: AppIds.SAML,
  microsoft: AppIds.MICROSOFT,
};

export const saveResponseToStorage = async (
  response: RefreshUserResponseType | QueryResponseType | AuthResponseType,
  appName?: AppsNamesType | "saml",
  ignoreUpdateCountryCodes?: boolean
) => {
  const storage = container.get<IStorage>(InjectStorage);
  const logger = container.get(FrontLogger).getLogger("cmd-utils");
  if (response?.user) {
    // save storage keys before any other data. in desktop these keys are used to encrypt data
    await storage.set({ [STORAGE.USER_ID_STORAGE_KEY]: response.user.userId, [STORAGE.ORG_ID_STORAGE_KEY]: response.user.organizationId });

    await storage.set(STORAGE.USER, response.user);
    if (response.user.userPlatform) {
      await storage.set(STORAGE.PLATFORM, response.user.userPlatform);
    } else {
      await storage.remove(STORAGE.PLATFORM);
    }

    if (response.user.deleteCRMSessionOnLogoutOnDesktop) {
      await storage.set(STORAGE.DELETE_CRM_SESSION_ON_LOGOUT, response.user.deleteCRMSessionOnLogoutOnDesktop);
    } else {
      await storage.remove(STORAGE.DELETE_CRM_SESSION_ON_LOGOUT);
    }

    if (response.user.desktopLimitNumberOfChannels) {
      await storage.set(STORAGE.LIMIT_NUMBER_OF_CHANNELS, response.user.desktopLimitNumberOfChannels);
    } else {
      await storage.remove(STORAGE.LIMIT_NUMBER_OF_CHANNELS);
    }

    if (response.user.onlyDesktopLiteVersion) {
      await storage.set(STORAGE.DESKTOP_LITE, response.user.onlyDesktopLiteVersion);
    } else {
      await storage.remove(STORAGE.DESKTOP_LITE);
    }
    if (response.user.disable_save_as_at_desktop) {
      await storage.set(STORAGE.DISABLE_SAVE_AS_AT_DESKTOP, response.user.disable_save_as_at_desktop);
    } else {
      await storage.remove(STORAGE.DISABLE_SAVE_AS_AT_DESKTOP);
    }

    if (response.user.eliteSecurityToken) {
      await storage.set(STORAGE.ELITE_SECURITY_TOKEN, response.user.eliteSecurityToken);
    } else {
      await storage.remove(STORAGE.ELITE_SECURITY_TOKEN);
    }
    if (response.user.eliteSecurityTokenUserAgentHeader) {
      await storage.set(STORAGE.ELITE_SECURITY_TOKEN_USER_AGENT_HEADER, true);
    } else {
      await storage.remove(STORAGE.ELITE_SECURITY_TOKEN_USER_AGENT_HEADER);
    }
    await storage.set(STORAGE.BASE_URL, response.user.base ?? "");
  }

  if (response?.success) {
    updateDLPPoliciesService.updateStorageLastDLPPoliciesUpdate(storage);
    if ("dlpSettings" in response && response.dlpSettings) {
      await storage.set(STORAGE.DLP_SETTINGS, response.dlpSettings);
    }
    if ("surveillanceSettings" in response && response.surveillanceSettings) {
      await storage.set(STORAGE.SURVEILLANCE_SETTINGS, response.surveillanceSettings);
    }
    updateKeywordPoliciesService.updateStorageLastKeywordPoliciesUpdate(storage);
    if ("keywordInsightsSettings" in response && response.keywordInsightsSettings) {
      await storage.set(STORAGE.KEYWORD_INSIGHTS_SETTINGS, response.keywordInsightsSettings);
    }
    if ("etoken" in response && response.etoken) {
      await storage.set(STORAGE.ETOKEN_KEY, response.etoken);
    }
    await storage.set(STORAGE.POD_KEY, response.wPod ?? "us1");
  }

  let loginProvider: LoginProviderType | undefined = appName ? providers[appName] : (response.user?.loginProvider ?? undefined);
  if (
    "appName" in response &&
    (response.appName === "salesforce" || response.appName === "msdynamics" || response.appName === "hubspot" || response.appName === AppIds.GOOGLE)
  ) {
    loginProvider = (() => {
      if (response.appName === AppIds.GOOGLE) {
        return AppIds.GOOGLE;
      }
      if (response.appName === "hubspot") {
        return AppIds.HUBSPOT;
      }
      if (response.appName === "msdynamics") {
        return AppIds.MICROSOFT_DYNAMICS;
      }
      return AppIds.SALESFORCE;
    })();
  }
  if (loginProvider) {
    await storage.set(STORAGE.LOGIN_PROVIDER, loginProvider);
  }
  !ignoreUpdateCountryCodes && (await updateCountryCodes().catch((err) => logger.error({ err }, "failed to get and save codes")));

  // set after all init becuase we need userId and orgId
  if (response.success && "urlPoliciesSettings" in response && response.urlPoliciesSettings) {
    updateUrlPoliciesService.updateStorageLastUrlPoliciesUpdate(storage);
    await storage.set(STORAGE.URL_POLICIES_SETTINGS, response.urlPoliciesSettings);
  }
};

export async function updateCountryCodes({ signal }: { signal?: AbortSignal } = {}) {
  const fetchMethod = container.get(FetchMethod);
  const storage = container.get<IStorage>(InjectStorage);

  const { etoken, whatsllyPod } = await storage.get([STORAGE.ETOKEN_KEY, STORAGE.POD_KEY]);
  const codesRequest = await getJSON({
    pod: whatsllyPod,
    urlSuffix: "/codes/v2",
    etoken,
    fetchMethod,
    signal,
  });
  const codesResponse = await codesRequest.json();
  if (codesResponse.codes && codesResponse.regex) {
    await storage.set({
      [STORAGE.COUNTRY_CODES_KEY]: codesResponse,
      [STORAGE.CODES_KEY]: codesResponse.codes,
      [STORAGE.REGEX_KEY]: codesResponse.regex,
    });
  }
}

export const cleanStorage = async () => {
  const storage = container.get<IStorage>(InjectStorage);
  const logger = container.get(FrontLogger).getLogger("cmd-utils");
  // TODO remove STORAGE.SFDC_SETTINGS after closet release
  await storage
    .remove([
      STORAGE.ETOKEN_KEY,
      STORAGE.USER,
      STORAGE.SFDC_SETTINGS,
      STORAGE.LOGIN_PROVIDER,
      STORAGE.RECENTLY_PERSON_SEARCH,
      STORAGE.USER_LAST_DLP_POLICIES_UPDATE,
      STORAGE.USER_LAST_SURVEILLANCE_POLICIES_UPDATE,
      STORAGE.USER_LAST_KEYWORD_CAMPAIGNS_UPDATE,
      STORAGE.USER_URL_POLICIES_SETTINGS_UPDATE,
    ])
    .catch((err) => {
      logger.error({ err }, "failed to clean data");
    });
};
